import { useCallback, useMemo } from "react";
import { JournalEntryDrawerFormikConfig } from "@components/accounting/journalEntryDrawer/JournalEntryDrawer";
import {
    ChangedCells,
    SpreadsheetGrid,
} from "@components/spreadsheet/SpreadsheetGrid";
import { ColumnDef, SpreadsheetData } from "@components/spreadsheet/types";
import { CellType, BaseCellProps } from "@components/spreadsheet/cells";
import { FormikErrors, useFormikContext } from "formik";
import {
    useIsManualJournalEntry,
    useIsTransactionJournalEntry,
} from "@components/accounting/journalEntryDrawer/JournalEntryDrawerHooks";
import { useManualJournalAccounts } from "@hooks/useManualJournalAccounts";
import classNames from "classnames";
import { sumMonetaryValues } from "@common/helpers/monetary";
import { useClasses } from "@api/class.api";
import { useCounterpartyNameMap } from "@components/counterparty/useCounterpartyNameMap";
import {
    changeDataAfterAccountChanged,
    changeDataAfterAmountChanged,
} from "@components/accounting/journalEntryDrawer/helpers";
import { currencyFormatter } from "@common/helpers/currency";
import { useIsDateLocked } from "@hooks/useIsDateLocked";
import { toUTCDateOnly } from "@common/helpers/date";
import { useTransactionsByIds } from "@components/transactions/TransactionDetails/useTransactionDetailsQuery";
import { getEntityIdForGLAccount } from "@hooks/useEntityIdForGLAccount";
import { useEntities } from "@hooks/useEntities";
import { getIntegerSourceId } from "@common/helpers/journalEntry";
import {
    CreateAccountModalParams,
    useAccountCreationCta,
} from "@components/accounting/journalEntryDrawer/useAccountCreationCta";
import { TransactionSplitCounterpartyCell } from "@components/transactions/TransactionSplitV2/TransactionSplitCounterpartyCell";
import { TransactionSplitClassCell } from "@components/transactions/TransactionSplitV2/TransactionSplitClassCell";
import {
    buildEmptyLines,
    spreadsheetRowToJournalEntryLine,
} from "./JournalEntryDrawerUtils";
import {
    JE_COLUMNS,
    buildJeGridTemplateColumns,
} from "./journalEntrySpreadsheetColumns";
import {
    collectUniqueLineTransactionIds,
    getFinancialJournalEntryRowIndex,
    getNonFinancialJournalEntryRowIndex,
} from "./journalEntryTransactionLines";
import {
    applyDisplayLinesToSource,
    collapseUneditableJournalEntryLines,
    getUneditableFinancialRowIndices,
} from "./journalEntryLineCollapse";
import styles from "./JournalEntryDrawer.module.scss";

type JournalLineErrors = Array<
    FormikErrors<
        JournalEntryDrawerFormikConfig["journalEntry"]["lines"][number]
    >
>;

interface Props {
    formEntityId: number;
    onEnterEditMode: () => void;
    onExitEditMode: () => void;
    onOpenCreateAccount: (params: CreateAccountModalParams) => void;
    allowedAccountIds?: Set<string>;
    /** Fields that should be hidden from the spreadsheet. */
    hiddenFields?: ReadonlySet<string>;
}

export const JournalEntrySpreadsheet = ({
    formEntityId,
    onEnterEditMode,
    onExitEditMode,
    onOpenCreateAccount,
    allowedAccountIds,
    hiddenFields,
}: Props) => {
    const formikContext = useFormikContext<JournalEntryDrawerFormikConfig>();
    const { values, setFieldValue, errors } = formikContext;
    const { lines, memo } = values.journalEntry;

    const lineErrors = errors.journalEntry?.lines as
        | JournalLineErrors
        | undefined;

    const isLocked = useIsDateLocked(
        formEntityId,
        values.journalEntryId && values.journalEntry.date
            ? toUTCDateOnly(values.journalEntry.date)
            : undefined,
    );

    const isManualJournal = useIsManualJournalEntry(values);
    const isTransactionJournal = useIsTransactionJournalEntry(values);

    const isReversedJournalEntry =
        !!values.journalEntry.reversedManualJournalEntryId;
    const disabled =
        isReversedJournalEntry ||
        isLocked ||
        !(isManualJournal || isTransactionJournal);

    const transactionIdsToLoad = useMemo(() => {
        const fromLines = collectUniqueLineTransactionIds(lines);
        if (fromLines.length > 0) {
            return fromLines;
        }
        const sourceId = getIntegerSourceId(values.journalEntry.source);
        if (isTransactionJournal && sourceId) {
            return [sourceId];
        }
        return [];
    }, [isTransactionJournal, lines, values.journalEntry.source]);
    const { transactions: journalEntryTransactions, transactionsById } =
        useTransactionsByIds(transactionIdsToLoad);
    const entities = useEntities();

    const { displayLines, displayToSourceIndices } = useMemo(() => {
        const shouldCollapseDuplicateAccountSides =
            !!values.journalEntry.isCombinedSplitJournalEntry ||
            transactionIdsToLoad.length > 1;
        if (!shouldCollapseDuplicateAccountSides) {
            return {
                displayLines: lines,
                displayToSourceIndices: lines.map((_, index) => [index]),
            };
        }
        return collapseUneditableJournalEntryLines({
            lines,
            uneditableRowIndices: getUneditableFinancialRowIndices(
                lines,
                journalEntryTransactions,
            ),
        });
    }, [
        journalEntryTransactions,
        lines,
        transactionIdsToLoad.length,
        values.journalEntry.isCombinedSplitJournalEntry,
    ]);

    const { accounts: allAccounts } = useManualJournalAccounts(
        formEntityId,
        values.ledgerBasis,
    );

    const accounts = useMemo(
        () =>
            allowedAccountIds
                ? allAccounts.filter((a) => allowedAccountIds.has(a.id))
                : allAccounts,
        [allAccounts, allowedAccountIds],
    );

    const { isActivated: isClassesActivated } = useClasses();

    const counterpartiesMap = useCounterpartyNameMap();

    const { accountCta } = useAccountCreationCta({
        accounts,
        lines,
        onOpenCreateAccount,
    });

    // Full column definitions keyed by field so they can be filtered by hiddenFields.
    const allColumns: ColumnDef[] = useMemo(
        () => [
            { field: "", label: "", cellType: CellType.ROW_NUMBER },
            {
                field: "accountId",
                label: "Account",
                cellType: CellType.ACCOUNT_SEARCH,
                accounts,
                cta: disabled ? undefined : accountCta,
                aggregation: () => "Total",
                isValid: ({ row }) => !lineErrors?.[row]?.accountId,
            },
            {
                field: "counterpartyId",
                label: "Counterparty",
                cellType: CellType.COUNTERPARTY,
                counterpartiesMap,
                cellFn: (props) => {
                    const stringValue =
                        typeof props.value === "string" || props.value === null
                            ? props.value
                            : null;
                    return (
                        <TransactionSplitCounterpartyCell
                            {...props}
                            value={stringValue}
                            counterpartiesMap={counterpartiesMap}
                        />
                    );
                },
            },
            ...(isClassesActivated
                ? [
                      {
                          field: "classIds",
                          label: "Class",
                          cellType: CellType.CLASS,
                          cellFn: (props: BaseCellProps) => {
                              const classValue =
                                  Array.isArray(props.value) ||
                                  props.value === null
                                      ? props.value
                                      : null;
                              return (
                                  <TransactionSplitClassCell
                                      {...props}
                                      value={classValue}
                                  />
                              );
                          },
                      },
                  ]
                : []),
            {
                field: "debitAmount",
                label: "Debit",
                cellType: CellType.MATH,
                isMonetary: true,
                aggregation: (data) => {
                    const sum = sumMonetaryValues(
                        data.map(({ debitAmount }) => debitAmount ?? 0),
                    );

                    return currencyFormatter.format(sum);
                },
                isValid: ({ row }) => !lineErrors?.[row]?.debitAmount,
            },
            {
                field: "creditAmount",
                label: "Credit",
                cellType: CellType.MATH,
                isMonetary: true,
                aggregation: (data) => {
                    const sum = sumMonetaryValues(
                        data.map(({ creditAmount }) => creditAmount ?? 0),
                    );

                    return currencyFormatter.format(sum);
                },
                isValid: ({ row }) => !lineErrors?.[row]?.creditAmount,
            },
            {
                field: "excludeFrom1099",
                label: "Exclude from 1099",
                cellType: CellType.BOOLEAN,
            },
            {
                field: "description",
                label: "Description",
                cellType: CellType.STRING,
            },
        ],
        [
            lineErrors,
            accounts,
            isClassesActivated,
            counterpartiesMap,
            disabled,
            accountCta,
        ],
    );

    const columns = useMemo(
        () =>
            hiddenFields?.size
                ? allColumns.filter((col) => !hiddenFields.has(col.field))
                : allColumns,
        [allColumns, hiddenFields],
    );

    const gridTemplateColumns = useMemo(() => {
        const visibleMeta = JE_COLUMNS.filter((meta) =>
            columns.some((col) => col.field === meta.field),
        );
        return buildJeGridTemplateColumns(visibleMeta);
    }, [columns]);

    const isCellEditable = useCallback(
        (rowIdx: number, colIdx: number) => {
            if (disabled) {
                return false;
            }

            if (!isTransactionJournal) {
                return true;
            }

            const field = columns[colIdx]?.field;
            const isEditableColumn =
                field === "accountId" || field === "classIds";
            if (!isEditableColumn) {
                return false;
            }

            const sourceIndices = displayToSourceIndices[rowIdx];
            if (!sourceIndices || sourceIndices.length !== 1) {
                return false;
            }
            const sourceRowIdx = sourceIndices[0];
            const lineTransactionId = lines[sourceRowIdx]?.transactionId;
            const part =
                lineTransactionId != null
                    ? transactionsById.get(lineTransactionId)
                    : journalEntryTransactions[0];
            if (!part) {
                return false;
            }

            const glAccountEntityId = getEntityIdForGLAccount(part, entities);
            return (
                glAccountEntityId === part.entity.id &&
                sourceRowIdx ===
                    getNonFinancialJournalEntryRowIndex(lines, part)
            );
        },
        [
            columns,
            disabled,
            displayToSourceIndices,
            entities,
            isTransactionJournal,
            journalEntryTransactions,
            lines,
            transactionsById,
        ],
    );

    const handleChange = useCallback(
        (newData: SpreadsheetData, changedCells: ChangedCells) => {
            // Normalise rows that were created by pasting beyond the current
            // data length — they arrive as partial objects (missing id, etc.).
            const originalLength = displayLines.length;
            let normalizedData = newData.map((row, idx) => {
                if (idx < originalLength || (row && row.id)) {
                    return row;
                }
                return {
                    ...buildEmptyLines(1)[0],
                    ...row,
                };
            });

            for (const cell of changedCells) {
                const [rowIdx, colIdx] = cell;
                // Skip spare-row indices — they have no data to post-process.
                if (rowIdx >= normalizedData.length) {
                    continue;
                }
                const { field } = columns[colIdx];

                if (field === "accountId") {
                    normalizedData = changeDataAfterAccountChanged(
                        normalizedData,
                        rowIdx,
                        memo,
                    );
                }

                if (field === "debitAmount") {
                    normalizedData = changeDataAfterAmountChanged(
                        normalizedData,
                        rowIdx,
                        "debitAmount",
                    );
                }

                if (field === "creditAmount") {
                    normalizedData = changeDataAfterAmountChanged(
                        normalizedData,
                        rowIdx,
                        "creditAmount",
                    );
                }
            }

            let nextSourceLines = applyDisplayLinesToSource({
                sourceLines: lines,
                displayLines: normalizedData.map(
                    spreadsheetRowToJournalEntryLine,
                ),
                displayToSourceIndices,
            });

            for (const cell of changedCells) {
                const [rowIdx, colIdx] = cell;
                if (
                    columns[colIdx]?.field !== "classIds" ||
                    !isTransactionJournal
                ) {
                    continue;
                }
                const sourceIndices = displayToSourceIndices[rowIdx];
                if (!sourceIndices || sourceIndices.length !== 1) {
                    continue;
                }
                const sourceRowIdx = sourceIndices[0];
                const lineTransactionId =
                    nextSourceLines[sourceRowIdx]?.transactionId;
                const part =
                    lineTransactionId != null
                        ? transactionsById.get(lineTransactionId)
                        : journalEntryTransactions[0];
                if (
                    !part ||
                    sourceRowIdx !==
                        getNonFinancialJournalEntryRowIndex(
                            nextSourceLines,
                            part,
                        )
                ) {
                    continue;
                }
                const financialLineIdx = getFinancialJournalEntryRowIndex(
                    nextSourceLines,
                    part,
                );
                const classIds = Array.isArray(
                    nextSourceLines[sourceRowIdx]?.classIds,
                )
                    ? [...nextSourceLines[sourceRowIdx].classIds]
                    : nextSourceLines[sourceRowIdx]?.classIds;
                if (
                    financialLineIdx === undefined ||
                    !nextSourceLines[financialLineIdx]
                ) {
                    continue;
                }
                nextSourceLines = [
                    ...nextSourceLines.slice(0, financialLineIdx),
                    {
                        ...nextSourceLines[financialLineIdx],
                        classIds,
                    },
                    ...nextSourceLines.slice(financialLineIdx + 1),
                ];
            }

            setFieldValue("journalEntry.lines", nextSourceLines);
        },
        [
            setFieldValue,
            columns,
            memo,
            isTransactionJournal,
            journalEntryTransactions,
            transactionsById,
            displayLines.length,
            displayToSourceIndices,
            lines,
        ],
    );

    return (
        <SpreadsheetGrid
            onExitEditMode={onExitEditMode}
            onEnterEditMode={onEnterEditMode}
            isCellEditable={isCellEditable}
            className={classNames(styles.spreadsheet, {
                [styles.editableSpreadsheet]:
                    !disabled || isReversedJournalEntry,
            })}
            stickyHeader
            showTotalsRow
            noOuterBorder
            minSpareRows={20}
            data={displayLines}
            columns={columns}
            onChange={handleChange}
            gridTemplateColumns={gridTemplateColumns}
            overflow="auto"
            lightNotEditableCells
        />
    );
};
